/**
 * HTML Content Display Component
 * Renders interactive HTML content (K-line charts, reports) via iframe
 * Supports both inline HTML (streaming) and URL-based loading (history)
 */

import React, { useState, useMemo } from 'react';
import { HtmlContentEvent } from '@/lib/api/chat';
import { Maximize2, Minimize2, X, BarChart3, Loader2 } from 'lucide-react';

interface HtmlContentDisplayProps {
  content: HtmlContentEvent;
  onClose?: () => void;
}

export const HtmlContentDisplay: React.FC<HtmlContentDisplayProps> = ({ content, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  const title = content.title || 'Interactive Content';

  // Create iframe src: use html_url for history, blob URL for streaming
  const iframeSrc = useMemo(() => {
    if (content.html_url) {
      return content.html_url;
    }
    if (content.html_content) {
      const blob = new Blob([content.html_content], { type: 'text/html' });
      return URL.createObjectURL(blob);
    }
    return null;
  }, [content.html_content, content.html_url]);

  const handleToggleExpand = () => setIsExpanded((prev) => !prev);

  // Expanded fullscreen view
  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-2xl max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              <span className="font-medium text-slate-800">{title}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleExpand}
                className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700"
                title="Exit fullscreen"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 relative" style={{ width: '90vw', height: '80vh' }}>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            )}
            {iframeSrc && (
              <iframe
                src={iframeSrc}
                className="w-full h-full border-0"
                sandbox="allow-scripts allow-same-origin"
                onLoad={() => setLoading(false)}
                title={title}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Inline compact view
  return (
    <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-slate-700">{title}</span>
        </div>
        <button
          onClick={handleToggleExpand}
          className="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-700"
          title="Expand"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>
      {/* Content preview */}
      <div className="relative" style={{ height: Math.min(content.height, 400) }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
        {iframeSrc ? (
          <iframe
            src={iframeSrc}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin"
            onLoad={() => setLoading(false)}
            title={title}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400">
            <span>No content available</span>
          </div>
        )}
      </div>
      {/* Click hint */}
      <div className="px-3 py-1.5 text-xs text-slate-400 text-center border-t border-slate-200">
        Click expand for full view
      </div>
    </div>
  );
};

export default HtmlContentDisplay;
